/* The eval atoms PublishGate needs, extracted verbatim from
   components/evals/atoms.jsx. PublishGate is a shared surface (the agent
   publish gate) but renders eval verdicts, so these two atoms have to sit below
   the observability feature. components/evals/atoms.jsx re-exports them, so the
   eval screens' own imports are unchanged. */

import React from "react";
import { ArrowDown, ArrowUp, Minus } from "lucide-react";

const STATUS_TONES: Record<string, string> = {
  pending: "bg-[var(--info-bg)] text-[var(--info-text)] ring-[var(--info-ring)]",
  pass: "bg-[var(--success-bg)] text-[var(--success)] ring-[var(--success-ring)]",
  fail: "bg-[var(--danger-bg)] text-[var(--danger)] ring-[var(--danger-ring)]",
  error: "bg-[var(--surface-2)] text-[var(--text-tertiary)] ring-[var(--hairline)]",
};
const STATUS_LABELS: Record<string, string> = { pending: "Running", pass: "Pass", fail: "Fail", error: "No signal" };

export function StatusPill({ status }: { status?: string }) {
  return (
    <span className={`inline-flex items-center h-5 px-2 rounded-full text-[11.5px] font-medium ${STATUS_TONES[status!] || STATUS_TONES.error}`}>
      {STATUS_LABELS[status!] || status}
    </span>
  );
}

/* Change against the baseline. Direction is unambiguous because every score
   means the same thing — higher is better, always. No per-metric setting. */
export function DeltaChip({ delta, className = "" }: { delta: number | null | undefined; className?: string }) {
  if (delta == null) {
    return <span className={`font-mono text-[11.5px] text-[var(--text-quaternary)] ${className}`}>—</span>;
  }
  const flat = Math.abs(delta) < 0.005;
  const Icon = flat ? Minus : delta > 0 ? ArrowUp : ArrowDown;
  const tone = flat
    ? "text-[var(--text-quaternary)]"
    : delta > 0
    ? "text-[var(--success)]"
    : "text-[var(--danger)]";
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono text-[11.5px] font-medium tabular-nums ${tone} ${className}`}>
      <Icon className="w-2.5 h-2.5" strokeWidth={2.5} />
      {flat ? "0.00" : `${delta > 0 ? "+" : "−"}${Math.abs(delta).toFixed(2)}`}
    </span>
  );
}
